/**
 * Puzzle provider: serves a puzzle instantly from the bundled, pre-graded
 * library, then refills the queue in the background via the generation worker
 * so play is effectively infinite and the UI never stalls.
 *
 * Falls back to on-demand worker generation when the library for a difficulty
 * runs dry.
 */

import libraryData from "../data/library.json";
import { Grid, parseGrid } from "../core/board.js";
import { DifficultyId } from "../core/difficulty.js";
import type { GeneratedPuzzle } from "../core/generator.js";
import type { WorkerRequest, WorkerResponse } from "./generator.worker.js";
import { load, save } from "./storage.js";

interface LibEntry {
  puzzle: string;
  solution: string;
  tier: number;
  clues: number;
}

const LIBRARY = libraryData as Record<string, LibEntry[]>;
const QUEUE_TARGET = 3;

function toGenerated(
  difficulty: DifficultyId,
  e: LibEntry,
  seed: string,
): GeneratedPuzzle {
  return {
    difficulty,
    puzzle: parseGrid(e.puzzle) as Grid,
    solution: parseGrid(e.solution) as Grid,
    grade: {
      solvableByLogic: true,
      tier: e.tier,
      difficulty,
      techniqueCounts: {},
      techniquesUsed: [],
    },
    clues: e.clues,
    seed,
    attempts: 0,
  };
}

export class PuzzleSource {
  private worker: Worker;
  private reqId = 0;
  private pending = new Map<number, (r: WorkerResponse) => void>();
  private queues: Record<string, GeneratedPuzzle[]> = {};
  private used: Record<string, string[]>;

  constructor() {
    this.worker = new Worker(
      new URL("./generator.worker.ts", import.meta.url),
      { type: "module" },
    );
    this.worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const resolve = this.pending.get(e.data.id);
      if (resolve) {
        this.pending.delete(e.data.id);
        resolve(e.data);
      }
    };
    this.used = load<Record<string, string[]>>("usedPuzzles", {});
    // Seed each difficulty's queue from unused library entries.
    for (const id of Object.keys(LIBRARY)) {
      const usedSet = new Set(this.used[id] ?? []);
      this.queues[id] = (LIBRARY[id] ?? [])
        .filter((e) => !usedSet.has(e.puzzle))
        .map((e) => toGenerated(id as DifficultyId, e, `lib:${id}`));
    }
  }

  /** Get a ready puzzle, then refill the queue in the background. */
  async get(difficulty: DifficultyId): Promise<GeneratedPuzzle> {
    const q = this.queues[difficulty] ?? (this.queues[difficulty] = []);
    let puzzle: GeneratedPuzzle;
    if (q.length > 0) {
      puzzle = q.shift()!;
    } else {
      puzzle = await this.generate(difficulty);
    }
    this.markUsed(difficulty, puzzle);
    void this.refill(difficulty);
    return puzzle;
  }

  /** Deterministic daily puzzle (always freshly generated for the date seed). */
  async daily(dateIso: string, difficulty: DifficultyId): Promise<GeneratedPuzzle> {
    return this.generate(difficulty, `daily:${dateIso}:${difficulty}`);
  }

  private markUsed(difficulty: string, p: GeneratedPuzzle): void {
    const key = p.puzzle.join("");
    const list = (this.used[difficulty] ??= []);
    list.push(key);
    if (list.length > 500) list.shift();
    save("usedPuzzles", this.used);
  }

  private async refill(difficulty: DifficultyId): Promise<void> {
    const q = this.queues[difficulty] ?? (this.queues[difficulty] = []);
    while (q.length < QUEUE_TARGET) {
      q.push(await this.generate(difficulty));
    }
  }

  private generate(difficulty: DifficultyId, seed?: string): Promise<GeneratedPuzzle> {
    const id = this.reqId++;
    const req: WorkerRequest = { id, difficulty, seed };
    return new Promise<GeneratedPuzzle>((resolve) => {
      this.pending.set(id, (r) =>
        resolve(
          toGenerated(difficulty, {
            puzzle: r.puzzle,
            solution: r.solution,
            tier: r.tier,
            clues: r.clues,
          }, seed ?? `gen:${difficulty}`),
        ),
      );
      this.worker.postMessage(req);
    });
  }
}
